import React from "react";
import { createPortal } from "react-dom";
import { HelpCircle } from "lucide-react";

// Klikací otazník s nápovědou (tooltip). Použijí ho všechny reporty konzistentně.
// Popup se renderuje přes React Portal přímo do document.body, aby NEDĚDIL CSS (uppercase,
// tracking-wider, font-bold) z parent elementů typu label v Funnel/KPI/Trenéři. Inline styl
// na popup je tedy jediný zdroj pravdy pro jeho typografii.
// 10.9.2026: `varianta="hlavicka"` je otazník do tmavé lišty stránky. Drobný
// šedý otazník tam byl na tmavém pozadí skoro neviditelný a na telefonu spadl
// pod nadpis na vlastní řádek. Ve variantě pro hlavičku je to kolečko stejné
// velikosti jako ostatní ikonové akce.
// 13.9.2026: `tecka` nahradí bílý otazník v rohu ikony barevnou tečkou stavu.
// Používá to Týdenní přehled: barva řekne, jestli období probíhá, je uzavřené
// nebo teprve přijde, a klepnutím se otevře stejná nápověda jako u otazníku.
const TONY_TECKY = {
  ok: "bg-emerald-400",
  neutralni: "bg-slate-300",
  prazdna: "bg-transparent ring-white/60",
};

export default function InfoTip({ text, varianta = "vedleTextu", ikona: Ikona, tecka }) {
  const [open, setOpen] = React.useState(false);
  const btnRef = React.useRef(null);
  const popupRef = React.useRef(null);
  // pos = { top, left, flipDown } — top/left jsou finální fixed pozice popup boxu (top-left roh).
  // Default off-screen (-9999) aby se popup neukázal v top-left rohu před useLayoutEffect.
  const [pos, setPos] = React.useState({ top: -9999, left: -9999, flipDown: false });

  const handleClick = (e) => {
    e.stopPropagation();
    setOpen((o) => !o);
  };

  // Po renderingu popup změř jeho rozměry a vypočítej pozici aby nepřetekl viewport.
  React.useLayoutEffect(() => {
    if (!open || !popupRef.current || !btnRef.current) return;
    const popupH = popupRef.current.offsetHeight;
    const popupW = popupRef.current.offsetWidth;
    const btnRect = btnRef.current.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const pad = 8;
    const gap = 8;

    // Vertikálně: default nad ikonkou, flip dolů pokud nestačí místo nahoře
    let top = btnRect.top - gap - popupH;
    let flipDown = false;
    if (top < pad) {
      flipDown = true;
      top = btnRect.bottom + gap;
      // Pokud ani dolů se nevejde (mělý viewport), clamp na top=pad
      if (top + popupH > vh - pad) top = Math.max(pad, vh - pad - popupH);
    }

    // Horizontálně: centered nad ikonkou, clamp aby se vešel
    let left = btnRect.left + btnRect.width / 2 - popupW / 2;
    if (left < pad) left = pad;
    if (left + popupW > vw - pad) left = vw - pad - popupW;

    setPos({ top, left, flipDown });
  }, [open]);

  // Zavři tooltip při: scroll, kolečko myši, pohyb myši přes okno, resize okna, Escape klávesa.
  // Mousemove zavře tooltip hned jak uživatel pohne myší = žádný tooltip neuvízne na obrazovce.
  React.useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    // Mousemove musí mít malé zpoždění aby se tooltip stihnul zobrazit — jinak by se
    // zavřel okamžitě při click+release (browser emituje mousemove hned po click)
    let mousemoveAttached = false;
    const attachMouseMove = () => {
      if (mousemoveAttached) return;
      mousemoveAttached = true;
      window.addEventListener("mousemove", close);
    };
    const timer = setTimeout(attachMouseMove, 250);
    window.addEventListener("scroll", close, true);
    window.addEventListener("wheel", close, { passive: true });
    window.addEventListener("resize", close);
    window.addEventListener("keydown", onKey);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("mousemove", close);
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("wheel", close);
      window.removeEventListener("resize", close);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // Portal popup — DOM parent je <body>, takže nic nedědí z labelu s uppercase.
  const popup = open ? createPortal(
    <>
      <div
        style={{ position: "fixed", inset: 0, zIndex: 40 }}
        onClick={() => setOpen(false)}
      />
      <div
        ref={popupRef}
        data-infotip-popup="1"
        className="infotip-popup"
        style={{
          position: "fixed",
          top: pos.top,
          left: pos.left,
          zIndex: 50,
          // Pre-measure: měříme se 14rem (224px) jako ideal width, ale max-width clamp
          // pro úzké viewporty (mobile <300px).
          width: "14rem",
          maxWidth: "calc(100vw - 16px)",
          padding: "8px 12px",
          borderRadius: "8px",
          background: "#31243C",
          boxShadow: "0 10px 15px -3px rgba(0,0,0,.1), 0 4px 6px -4px rgba(0,0,0,.1)",
        }}
      >
        {/* Inner div s `all: unset` — resetuje VŠECHNA inherited CSS properties na initial hodnoty
            (text-transform: none, letter-spacing: normal, font-weight: normal, atd.).
            Pak explicitně nastavím jen co má tooltip reálně vypadat.
            Toto je nejsilnější možný CSS reset — přebije Tailwind utility i s !important. */}
        <div
          style={{
            all: "unset",
            display: "block",
            color: "white",
            fontSize: "12px",
            lineHeight: 1.4,
            fontFamily: "system-ui, -apple-system, BlinkMacSystemFont, sans-serif",
            whiteSpace: "normal",
            wordWrap: "break-word",
          }}
        >
          {text}
        </div>
        {/* Šipka — pokud nad ikonkou (default), šipka dolů; pokud flipDown, šipka nahoru */}
        <div
          style={{
            position: "absolute",
            left: "50%",
            top: pos.flipDown ? undefined : "100%",
            bottom: pos.flipDown ? "100%" : undefined,
            transform: "translateX(-50%)",
            width: 0,
            height: 0,
            borderLeft: "4px solid transparent",
            borderRight: "4px solid transparent",
            borderTop: pos.flipDown ? undefined : "4px solid #31243C",
            borderBottom: pos.flipDown ? "4px solid #31243C" : undefined,
          }}
        />
      </div>
    </>,
    document.body
  ) : null;

  return (
    <span className="inline-flex items-center">
      <button
        ref={btnRef}
        type="button"
        onClick={handleClick}
        aria-expanded={open}
        className={
          varianta === "ikona"
            // Ikona stránky, která zároveň otevírá nápovědu. Rámeček je o odstín
            // světlejší než u ikony bez nápovědy a v rohu sedí bílý otazník,
            // aby bylo na první pohled poznat, že se na ni dá klepnout.
            ? `relative flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border bg-white/15 text-white transition focus:outline-none active:scale-95 sm:h-10 sm:w-10 sm:hover:bg-white/25 ${
                open ? "border-2 border-white bg-white/25" : "border-white/30"
              }`
            : varianta === "hlavicka"
              ? "flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-white/20 bg-white/10 text-white transition active:scale-95 active:bg-white/20 focus:outline-none sm:h-10 sm:w-10 sm:hover:bg-white/20"
              // 14. 9. 2026 (fáze 6): otazník má 12 px, na telefonu dostane
              // neviditelnou plochu 44 px kolem sebe (.dotyk-rozsiren), rozložení
              // popisku se nemění.
              : "dotyk-rozsiren ml-1 text-slate-300 hover:text-slate-500 transition-colors focus:outline-none"
        }
        aria-label="Nápověda"
        title="Nápověda"
      >
        {varianta === "ikona" && Ikona ? (
          <>
            <Ikona className="h-5 w-5" />
            {tecka ? (
              <span
                aria-hidden
                className={`absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full ring-2 ring-brand-950 ${TONY_TECKY[tecka] || TONY_TECKY.neutralni}`}
              />
            ) : (
              <span
                aria-hidden
                className="absolute -bottom-1 -right-1 flex h-[17px] w-[17px] items-center justify-center rounded-full bg-white text-[11px] font-black leading-none text-brand-deep shadow"
              >
                ?
              </span>
            )}
          </>
        ) : (
          <HelpCircle className={varianta === "hlavicka" ? "h-[18px] w-[18px]" : "w-3 h-3"} />
        )}
      </button>
      {popup}
    </span>
  );
}
